// Phase 3.3 — Procurement screen, migrated to shadcn/Tailwind.
//
// UI only: the MUI/antd Table, the ApparelSubmenu toolbar, the antd Drawer form
// and the antd delete Modal are replaced by the shared DataTable (via
// ProcurementTable), a PageHeader + built-in search/pagination + status/sort
// Select, a Sheet (ProcurementFormSheet) and ConfirmDialog. Every dispatch /
// useSelector / payload is unchanged — all handlers below are the legacy ones.
// The antd Form is dropped: the form is controlled by `procurementFormData`
// (which the antd fields mirrored), so the edit branch now populates that state
// fully; the submit payload is byte-for-byte the same.
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import ApparelMenu from "./SubComponents/apparelMenu";
import { useDispatch, useSelector } from 'react-redux';
import { categoryAndSubCategoryListAction, createOrUpdateProcurementAction, getProcurementAction, getProcurementExportListAction } from '../Store/actions/apparelAction';
import AppConstant from '../Globals/AppConstants';
import { ConfirmDialog } from '../shared/confirm-dialog';
import { Button } from 'ui/button';
import ProcurementTable from './inventory/ProcurementTable';
import ProcurementFormSheet from './inventory/ProcurementFormSheet';

const Procurement = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    // Arriving from Audit Log: fetch this one record directly by its
    // uniqueKey (no search box can isolate a procurement by code), then
    // open its edit Sheet once it shows up in the list — Procurement has a
    // real edit UI, so Audit Log goes straight to it.
    const [pendingAuditRecordId, setPendingAuditRecordId] = useState<string | undefined>(location.state?.auditRecordId);
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const {
        categoryAndSubCategoryData, getProcurementData, createOrUpdateProcurementOnLoad, grtProcurementOnLoad,
        createOrUpdateProcurementData, getProcurementExportListOnLoad, getProcurementExportListData
    } = apparelReducerState;
    const [procurementExportRequested, setProcurementExportRequested] = useState(false);
    const categoryAndSubCategoryList = categoryAndSubCategoryData?.getCategoryList;
    const [open, setOpen] = useState(false);
    const [procurementDeleteModalOpen, setProcurementDeleteModalOpen] = useState(false);
    const [createOrUpdateProcurement, setCreateOrUpdateProcurement] = useState(false);
    const [pendingProcurementIsEdit, setPendingProcurementIsEdit] = useState(false);
    const [statusUpdate, setStatusUpdate] = useState(false);
    const [procurementData, setProcurementData] = useState<any>(null);
    const [searchTemp, setSearchTemp] = useState({
        searchText: "",
        offset: 0,
        limit: 10,
        sort: 0,
        page: 1,
        categoryId: null,
        subCategoryId: null
    });

    let procurementList = getProcurementData?.getProcurementList?.procurementList;
    let procurementPage = getProcurementData?.getProcurementList?.page;
    let procurementMessage = createOrUpdateProcurementData?.createOrUpdateProcurement;


    const getsearchTextdataList = (searchText: any) => {
        setSearchTemp({ ...searchTemp, searchText: searchText, offset: 0 });
    }
    const handlePagination = (page: any, offset: any) => {
        setSearchTemp({ ...searchTemp, page: page, offset: offset });
    }

    // DataTable gives a 1-based page; translate to the legacy offset math.
    const onPageChange = (nextPage: number) => {
        let offset = ((nextPage * 10) - 10 === 0) ? 0 : (nextPage * 10) - 10;
        handlePagination(1, offset);
    }

    const [procurementFormData, setProcurementFormData] = useState<any>({
        procurementId: "",
        categoryId: null,
        subCategoryId: null,
        description: "",
        count: null,
        status: 1,
        imageFileUuid: null,
        isDeleted: 0
    })

    const resetData = () => {
        setProcurementFormData({
            ...procurementFormData,
            procurementId: "",
            categoryId: null,
            subCategoryId: null,
            description: "",
            count: null,
            status: 1,
            imageFileUuid: null,
            isDeleted: 0

        })
    }

    // Export button — always the full New + In Progress set, ignores whatever
    // filters are currently applied on screen.
    const saveProcurementReportAsPdfFile = () => {
        const pre = "data:application/pdf;base64,";
        const inp = getProcurementExportListData?.getProcurementExportList;
        if (!inp) return;
        const link = document.createElement("a");
        link.href = pre + inp;
        link.download = "Mukizh Procurement Report.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const exportProcurementList = () => {
        dispatch(getProcurementExportListAction());
        setProcurementExportRequested(true);
    }

    useEffect(() => {
        if (procurementExportRequested && !getProcurementExportListOnLoad) {
            setProcurementExportRequested(false);
            saveProcurementReportAsPdfFile();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getProcurementExportListOnLoad]);

    const getProcurementApi = () => {
        let payload = {
            searchText: searchTemp?.searchText,
            sortBy: searchTemp?.sort,
            categoryId: searchTemp?.categoryId,
            subCategoryId: searchTemp?.subCategoryId,
            procurementId: pendingAuditRecordId,
            paging: {
                limit: 10,
                offset: searchTemp?.offset
            }
        }
        dispatch(getProcurementAction(payload));

    }

    const getFilterCategorydataList = (categoryId: any) => {
        setSearchTemp({ ...searchTemp, categoryId: categoryId, subCategoryId: null, offset: 0 });
    }

    const getFilterSubCategorydataList = (subCategoryId: any) => {
        setSearchTemp({ ...searchTemp, subCategoryId: subCategoryId, offset: 0 });
    }

    useEffect(() => {
        getProcurementApi();
    }, [searchTemp])

    useEffect(() => {
        dispatch(categoryAndSubCategoryListAction(''));
        getProcurementApi()
    }, []);


    const createOrUpdateProcurementApi = () => {
        setPendingProcurementIsEdit(!!procurementFormData?.procurementId);
        let payload = {
            procurementId: procurementFormData?.procurementId,
            categoryId: procurementFormData?.categoryId ? procurementFormData?.categoryId : procurementData?.categoryId,
            subCategoryId: procurementFormData?.subCategoryId ? procurementFormData?.subCategoryId : procurementData?.subCategoryId,
            status: procurementFormData?.status ? procurementFormData?.status : procurementData?.status,
            count: procurementFormData?.count ? procurementFormData?.count : procurementData?.count,
            description: procurementFormData?.description ? procurementFormData?.description : procurementData?.description,
            imageFileUuid: procurementFormData?.imageFileUuid,
            isDeleted: procurementFormData?.isDeleted,
        }
        dispatch(createOrUpdateProcurementAction(payload));
        setOpen(false)
        setCreateOrUpdateProcurement(true);
        setProcurementData(null)
        resetData();
    }

    useEffect(() => {
        if (createOrUpdateProcurement && !createOrUpdateProcurementOnLoad) {
            if (procurementMessage == "Deleted Successfully") {
                toast.error(procurementMessage)
            }
            else {
                toast.success(pendingProcurementIsEdit ? "Procurement updated successfully" : "Procurement added successfully")
            }
            getProcurementApi();
            setCreateOrUpdateProcurement(false);
        }
    }, [createOrUpdateProcurementOnLoad, createOrUpdateProcurement]);

    // Category change → set categoryId + clear subCategoryId (legacy reset).
    const onCategoryChange = (categoryId: any) => {
        setProcurementFormData({ ...procurementFormData, categoryId: categoryId, subCategoryId: null });
    }

    const productClose = () => {
        setOpen(false);
        setProcurementFormData({ ...procurementFormData, categoryId: null });
    }

    useEffect(() => {
        if (statusUpdate) {
            createOrUpdateProcurementApi();
            setStatusUpdate(false);
        }
    }, [statusUpdate])

    useEffect(() => {
        if (!pendingAuditRecordId || !procurementList?.length) return;
        const match = procurementList.find((row: any) => row?.procurementId === pendingAuditRecordId);
        if (match) {
            procurementListAction("1", match);
            setPendingAuditRecordId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAuditRecordId, procurementList])

    const procurementListAction = (key: any, data: any) => {
        if (key == "1") {
            setProcurementData(data)
            setProcurementFormData({
                ...procurementFormData,
                procurementId: data?.procurementId,
                categoryId: data?.categoryId,
                subCategoryId: data?.subCategoryId,
                description: data?.description,
                count: data?.count,
                imageFileUuid: data?.imageFileUuid,
                status: data?.status,
                isDeleted: 0,
            })
            setOpen(true)
        }
        else if (Number(key) > 1 && Number(key) < 6) {
            setProcurementFormData({
                ...procurementFormData,
                procurementId: data?.procurementId,
                categoryId: data?.categoryId,
                subCategoryId: data?.subCategoryId,
                description: data?.description,
                count: data?.count,
                imageFileUuid: data?.imageFileUuid,
                status: Number(key),
                isDeleted: 0
            })
            setStatusUpdate(true)
        }
        else if (key == "6") {
            setProcurementFormData({
                ...procurementFormData,
                procurementId: data?.procurementId,
                categoryId: data?.categoryId,
                subCategoryId: data?.subCategoryId,
                description: data?.description,
                count: data?.count,
                imageFileUuid: data?.imageFileUuid,
                status: data?.status,
                isDeleted: 1
            })
            setProcurementDeleteModalOpen(true);
        }
    }

    const handleDeleteModal = () => {
        setStatusUpdate(true);
        setProcurementDeleteModalOpen(false);
    }

    const getFilterStatusdataList = (e: any) => {
        setSearchTemp({ ...searchTemp, sort: e });
    }

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="7" />
            <ProcurementTable
                data={procurementList}
                loading={grtProcurementOnLoad}
                onMenuAction={procurementListAction}
                searchValue={searchTemp?.searchText}
                onSearch={getsearchTextdataList}
                searchPlaceholder={"Search by category name"}
                sort={searchTemp?.sort}
                onSortChange={getFilterStatusdataList}
                categoryList={categoryAndSubCategoryList}
                categoryId={searchTemp?.categoryId}
                onCategoryFilterChange={getFilterCategorydataList}
                subCategoryId={searchTemp?.subCategoryId}
                onSubCategoryFilterChange={getFilterSubCategorydataList}
                title="Procurement"
                headerActions={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={exportProcurementList}
                            disabled={getProcurementExportListOnLoad}
                        >
                            {getProcurementExportListOnLoad ? "Exporting…" : "Export"}
                        </Button>
                        <Button onClick={() => setOpen(true)}>{AppConstant?.add}</Button>
                    </div>
                }
                pagination={{
                    page: procurementPage?.currentPage || 1,
                    total: procurementPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />
            <ProcurementFormSheet
                open={open}
                onClose={productClose}
                categoryList={categoryAndSubCategoryList}
                formData={procurementFormData}
                onCategoryChange={onCategoryChange}
                onChange={(patch: any) => setProcurementFormData({ ...procurementFormData, ...patch })}
                onSubmit={createOrUpdateProcurementApi}
            />
            <ConfirmDialog
                open={procurementDeleteModalOpen}
                onOpenChange={setProcurementDeleteModalOpen}
                title="Are you sure want to Delete"
                confirmLabel="Ok"
                destructive
                onConfirm={handleDeleteModal}
            />
        </div>
    )
}

export default Procurement
